import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type {
  ScopeActivationItem,
  ScopeActivationResponse,
} from "@/api/scope-activation";
import { Badge } from "@/components/ui/badge";
import { formatCount } from "@/lib/format";

import {
  confidenceLabel,
  countryAvailableItems,
  decisionGroups,
  UNKNOWN_LOB,
} from "./aderenciaData";
import { CapabilityEvidence } from "./CapabilityEvidence";

function ConfidenceLabel({ confidence }: { confidence: string }) {
  const { t } = useTranslation();
  if (!confidence) return null;
  const low = confidence === "baixa";
  return (
    <span
      className={`font-mono text-[10px] ${low ? "font-bold text-orange-600" : "text-muted-foreground"}`}
    >
      {t("confidence {{level}}", { level: t(confidenceLabel(confidence)) })}
    </span>
  );
}

function WorklistCard({ item }: { item: ScopeActivationItem }) {
  const { t } = useTranslation();
  // A worklist é o eixo Sugestão: todo item aqui é suggestion === "decision".
  // O "Review" do ECC (regra não compilável) vira selo de curadoria, não categoria.
  const needsCuration = item.verdict === "needs_review";
  return (
    <div className="rounded-lg border bg-white p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs font-bold text-primary">
          {item.scope_item_id}
        </span>
        <span className="text-sm font-semibold">
          {item.scope_item_name || "—"}
        </span>
        <Badge
          variant="secondary"
          className="bg-amber-100 text-[10px] text-amber-700"
        >
          {t("Decision")}
        </Badge>
        {item.suggestion_reason && (
          <span className="text-[11px] text-muted-foreground">
            {item.suggestion_reason}
          </span>
        )}
        {item.clean_core && (
          <span className="rounded bg-violet-100 px-1.5 py-0.5 font-mono text-[10px] font-bold text-violet-700">
            Clean Core
          </span>
        )}
        {needsCuration && (
          <span
            className="rounded bg-orange-100 px-1.5 py-0.5 font-mono text-[10px] font-bold text-orange-700"
            title={t("The classification rule failed to compile — needs curation")}
          >
            {t("Needs curation")}
          </span>
        )}
        <span className="ml-auto">
          <ConfidenceLabel confidence={item.confidence} />
        </span>
      </div>
      <div className="mt-2">
        <CapabilityEvidence
          item={item}
          emptyHint={t("No direct signal from ERP — depends on the client's scope decision.")}
        />
      </div>
    </div>
  );
}

/**
 * Identifica o tipo de resultado vazio para evitar falsos positivos de conformidade.
 * Usa `data.items` (mesmo predicado do `suggestion_status: "ok"` do backend) para diferenciar falha na árvore de resultado limpo.
 */
function emptyMessage(items: ScopeActivationItem[], t: (key: string) => string): string {
  if (items.length === 0) return t("No scope item was assessed in this run.");
  if (!items.some((i) => i.suggestion != null)) {
    return t(
      "The Fit-to-Standard suggestion is not available for this run — no item carries one, so none can be listed as a decision.",
    );
  }
  return t("No item needs a decision in this run. Everything fits the baseline or is out of scope.");
}

export function WorklistView({
  data,
  countryOnly = true,
}: {
  data: ScopeActivationResponse;
  countryOnly?: boolean;
}) {
  const { t } = useTranslation();
  const items = useMemo(
    () => countryAvailableItems(data.items, countryOnly),
    [data.items, countryOnly],
  );
  const groups = useMemo(() => decisionGroups(items), [items]);
  const total = useMemo(
    () => groups.reduce((acc, g) => acc + g.items.length, 0),
    [groups],
  );

  if (total === 0) {
    return (
      <div className="rounded-lg border bg-white p-6 text-center text-xs text-muted-foreground">
        {emptyMessage(data.items, t)}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <p className="text-xs text-muted-foreground">
        {t("Only the")}{" "}
        <span className="font-semibold text-foreground">{formatCount(total)}</span>{" "}
        {t("suggested as")}{" "}
        <span className="font-semibold text-foreground">{t("Decision")}</span>{" "}
        {t(
          "— out of baseline, to confirm with the client. Grouped by workshop track, low confidence and Clean Core on top.",
        )}
      </p>
      {groups.map((g) => (
        <div key={g.lob} className="flex flex-col gap-2.5">
          <div className="flex items-baseline gap-2">
            <h3 className="text-sm font-bold">{g.lob === UNKNOWN_LOB ? t(UNKNOWN_LOB) : g.lob}</h3>
            <span className="font-mono text-[11px] font-bold text-amber-600">
              {g.decisions}
            </span>
          </div>
          {g.items.map((item) => (
            <WorklistCard key={item.scope_item_id} item={item} />
          ))}
        </div>
      ))}
    </div>
  );
}
